import '../styles/styles.css';

import {
  appWithTranslation,
  fetchI18nResources,
  getNextI18NextConfig,
} from '@flex-packages/i18n';
import { LoggerContextProvider } from '@flex-packages/logger';
import { captureException, init } from '@flex-packages/sentry';
import { ReactQueryProvider } from '@flex-services/react-query-provider';
import { dehydrate, QueryClient } from '@tanstack/react-query';
import type { AppProps as NextAppProps } from 'next/app';
import getConfig from 'next/config';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import type { InitialProps } from '../src';
import { AxiosContextProvider, CommonsProvider, getInitialProps } from '../src';

const isServer = __FLEX_GLOBAL_CONFIG__.NEXT_IS_SERVER;
const isProductionBuild = __FLEX_GLOBAL_CONFIG__.IS_PRODUCTION_BUILD;
const appVersion = __FLEX_GLOBAL_CONFIG__.APP_VERSION;

interface Props extends NextAppProps, InitialProps {}

function App({ Component, serverSideProps, pageProps }: Props) {
  const { locale, now, userAgent, amplitudeUserProperties, flexUTMs } =
    serverSideProps;

  const [queryClient] = useState(() => new QueryClient());

  const router = useRouter();
  const [isRouterReady, setIdRouterReady] = useState(false);

  useEffect(() => {
    init({
      isProductionBuild,
      isServer,
      dsn: getConfig().publicRuntimeConfig.SENTRY_DSN,
      release: getConfig().publicRuntimeConfig.NEXT_PUBLIC_SENTRY_RELEASE,
      environment: getConfig().publicRuntimeConfig.STAGE,
    });
  }, []);

  useEffect(() => {
    setIdRouterReady(router.isReady);
  }, [router.isReady]);

  return (
    <AxiosContextProvider locale={locale}>
      <CommonsProvider locale={locale} now={now} userAgent={userAgent}>
        <ReactQueryProvider dehydratedState={dehydrate(queryClient)}>
          <LoggerContextProvider
            enabled={isRouterReady}
            serverUrl={getConfig().publicRuntimeConfig.AMPLITUDE_SERVER_URL}
            apiKey={getConfig().publicRuntimeConfig.AMPLITUDE_API_KEY}
            userProperties={amplitudeUserProperties}
            // 모든 Logger 이벤트에 flexUTMs를 view,click,impression eventProperties 에 첨부
            flexUTMs={flexUTMs}
            defaultEventProperties={{
              appVersion,
            }}
            onInitializeError={error => {
              captureException(new Error('Amplitude 초기화에 실패했습니다.'), {
                level: 'error',
                extra: { error },
              });
            }}
            options={{
              dryRun: !isProductionBuild,
            }}
          >
            <Component key={locale} {...pageProps} />
          </LoggerContextProvider>
        </ReactQueryProvider>
      </CommonsProvider>
    </AxiosContextProvider>
  );
}

App.getInitialProps = getInitialProps;

export default appWithTranslation(
  App,
  getNextI18NextConfig(fetchI18nResources(require.context('../locales')))
);
